import type { FunctionComponent } from 'react'

import {
	BodySStrong,
	CaptionS,
	H2,
	ICON_SIZE,
	PrimaryButton,
	SPACING,
} from '@monthio/design-system'
import { ExpandLess } from '@monthio/icons'
import { CurrencyDisplay, TextInputField } from '@monthio/react-components'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import type {
	SavingsCapacityExtraordinaryItemGroupDto,
	SavingsCapacityExtraordinaryTransactionItemDto,
} from '../SavingsCapacity.types'

import { ExtraordinaryItemsGroupBasePage } from './ExtraordinaryItemsGroupBasePage'
import { InputContainer, InputRowContainer } from './SharedStyledComponents'
import {
	TransactionListContainer,
	TransactionListItem,
} from './TransactionListItem'

const StyledH2 = styled(H2)`
	margin: 0;
	font-weight: 600;
`

const TransactionCountCaption = styled(CaptionS)`
	margin: 0;
`

const IconWrapper = styled.span<{ $isExpanded: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;

	> svg {
		height: ${ICON_SIZE.l};
		width: ${ICON_SIZE.l};
		transition: rotate 0.2s ease-in-out;
		rotate: ${({ $isExpanded }) => ($isExpanded ? '0deg' : '180deg')};
	}
`

const AccordionContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0px;
`

const AmountAndExpandIconContainer = styled.div`
	display: flex;
	align-items: center;
	flex-direction: row;
	gap: ${SPACING.xs};
`

interface ExtraordinaryItemsSelectionSummaryPageProps {
	extraordinaryItemGroup: SavingsCapacityExtraordinaryItemGroupDto
	onNext: (notes: string) => void
	onPrevious: () => void
	onSelect: (transactionId: string, selected: boolean) => void
}

export const ExtraordinaryItemsSelectionSummaryPage: FunctionComponent<
	ExtraordinaryItemsSelectionSummaryPageProps
> = ({ extraordinaryItemGroup, onNext, onPrevious, onSelect }) => {
	const { t } = useTranslation()
	const [notes, setNotes] = useState(extraordinaryItemGroup.notes)
	const [isExpanded, setIsExpanded] = useState(false)
	const [allItems, setAllItems] = useState(extraordinaryItemGroup.items)

	useEffect(() => {
		const newItems = extraordinaryItemGroup.items
			.filter((item) => item.type === 'transaction')
			.filter(
				(item: SavingsCapacityExtraordinaryTransactionItemDto) =>
					!allItems
						.filter((item) => item.type === 'transaction')
						.some(
							(existingItem) =>
								existingItem.transactionId === item.transactionId,
						),
			)
		if (newItems.length > 0) {
			setAllItems(newItems)
		}
	}, [allItems, extraordinaryItemGroup.items])

	const canContinue = notes !== ''

	const handleSave = useCallback(() => {
		onNext(notes)
	}, [notes, onNext])

	const selectedItems = extraordinaryItemGroup.items
	const totalAmount = selectedItems.reduce((acc, item) => acc + item.amount, 0)

	return (
		<ExtraordinaryItemsGroupBasePage
			buttons={[
				<PrimaryButton
					key="save"
					onClick={handleSave}
					disabled={!canContinue}
					size="xl"
				>
					{t('savingsCapacity-extraordinaryItemsSummary-continue-button')}
				</PrimaryButton>,
			]}
			onGoBackClick={onPrevious}
			category={extraordinaryItemGroup.type}
			title={t('savingsCapacity-extraordinaryItemsSummary-title')}
			subtitle={t('savingsCapacity-extraordinaryItemsSummary-subtitle')}
		>
			<AccordionContainer>
				<InputContainer onClick={() => setIsExpanded((prev) => !prev)}>
					<InputRowContainer>
						<div>
							<StyledH2>
								{t(
									'savingsCapacity-extraordinaryItemsSummary-selectedTransactions-label',
								)}
							</StyledH2>
							<TransactionCountCaption>
								{t(
									'savingsCapacity-extraordinaryItemsSummary-selectedTransactions-count-label',
									{
										count: selectedItems.length,
									},
								)}
							</TransactionCountCaption>
						</div>
						<AmountAndExpandIconContainer>
							<StyledH2>
								<CurrencyDisplay amount={totalAmount} symbolMode />
							</StyledH2>
							<IconWrapper $isExpanded={isExpanded}>
								<ExpandLess aria-hidden="true" />
							</IconWrapper>
						</AmountAndExpandIconContainer>
					</InputRowContainer>
				</InputContainer>
				{isExpanded && (
					<TransactionListContainer>
						{allItems
							.filter((item) => item.type === 'transaction')
							.map((item: SavingsCapacityExtraordinaryTransactionItemDto) => (
								<TransactionListItem
									key={item.transactionId}
									transactionId={item.transactionId ?? ''}
									text={item.description}
									date={item.transactionDate?.toString() ?? ''}
									amount={item.amount}
									isSelected={selectedItems
										.filter((item) => item.type === 'transaction')
										.some(
											(selectedItem) =>
												selectedItem.transactionId === item.transactionId,
										)}
									onSelect={() =>
										onSelect(
											item.transactionId ?? '',
											!selectedItems
												.filter((item) => item.type === 'transaction')
												.some(
													(selectedItem) =>
														selectedItem.transactionId === item.transactionId,
												),
										)
									}
								/>
							))}
					</TransactionListContainer>
				)}
			</AccordionContainer>

			<div>
				<BodySStrong>
					{t(
						'savingsCapacity-extraordinaryItemsTransactions-manualEntry-comment-label',
					)}
				</BodySStrong>
				<InputContainer>
					<TextInputField
						placeholder={t(
							'savingsCapacity-extraordinaryItemsTransactions-manualEntry-comment-placeholder',
						)}
						value={notes}
						multiline
						rows={4}
						onChange={(e) => setNotes(e.target.value)}
						InputProps={{ disableUnderline: true }}
					/>
				</InputContainer>
			</div>
		</ExtraordinaryItemsGroupBasePage>
	)
}
